import { useTu } from "@src/appV2/i18n/useTu";
import { Stack, Tooltip } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { useOmnisearch } from "@src/appV2/Omnisearch";
import { useNavHeader } from "../../Shared/Header/useNavHeader";
import { type ButtonVariant } from "@src/appV2/redesign/components/Button";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { ThemeModeToggle } from "@src/appV2/UserPreferences/components/ThemeModeToggle";
import { useThemeMode } from "@src/appV2/UserPreferences/UserPreferencesProvider";
import { useSwitchToAdmin } from "../../Shared/AccountSwitcher/useSwitchToAdmin";
import { ContactSupportButton } from "../../Shared/Header/ContactSupportButton";
import { HelpButton } from "../../Shared/Header/HelpButton";

interface NavigationMenuFooterMobileProps {
  closeSidenav: () => void;
}

interface AdminActionsProps {
  closeSidenav: () => void;
  navLinkVariant: ButtonVariant;
}

function AdminActions(props: AdminActionsProps) {
  const { tu } = useTu("navigation");
  const { closeSidenav, navLinkVariant } = props;
  const { isImpersonating, switchToAdmin } = useSwitchToAdmin();
  const { open: openOmnisearch } = useOmnisearch();

  return (
    <>
      {isImpersonating && (
        <Tooltip title={tu("Switch to admin")}>
          <span>
            <IconButton
              variant={navLinkVariant}
              iconType="person-male-formal"
              size="large"
              aria-label={tu("Switch to admin")}
              onClick={() => {
                switchToAdmin();
                closeSidenav();
              }}
            />
          </span>
        </Tooltip>
      )}

      <Tooltip title={tu("Search agents or workplaces")}>
        <span>
          <IconButton
            variant={navLinkVariant}
            iconType="magnifying-glass"
            size="large"
            aria-label={tu("Open search")}
            onClick={openOmnisearch}
          />
        </span>
      </Tooltip>

      <ThemeModeToggle size="large" />
    </>
  );
}

export function NavigationMenuFooterMobile(props: NavigationMenuFooterMobileProps) {
  const { closeSidenav } = props;
  const { themeMode } = useThemeMode();
  const { admin } = useSession();
  const isDark = themeMode === "dark";
  const navLinkVariant: ButtonVariant = isDark ? "linkInverted" : "link";

  const { displayHelpButton, onClickHelpButton } = useNavHeader();

  return (
    <>
      <Stack
        flexGrow={0}
        flexShrink={0}
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        gap={3}
      >
        {admin ? (
          <AdminActions closeSidenav={closeSidenav} navLinkVariant={navLinkVariant} />
        ) : (
          <>
            {displayHelpButton ? (
              <HelpButton
                size="large"
                onClickHelpButton={() => {
                  onClickHelpButton();
                  closeSidenav();
                }}
              />
            ) : (
              <ContactSupportButton />
            )}

            <ThemeModeToggle size="large" />
          </>
        )}
      </Stack>
    </>
  );
}
